import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useToast } from "../../../components/ui/use-toast";
import { adminGetProduct, adminCreateProduct, adminUpdateProduct } from '../../../services/admin/shopAdminService';
import { generateSlug } from '../../../services/shopService';
import { supabase } from '../../../lib/supabase';
import { Product as ProductType } from '../../../services/shopService';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
// @ts-ignore - Le composant existe mais TypeScript ne le trouve pas
import { Switch } from '../../../components/ui/switch';
import { X, Loader2, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { MediaSelector } from '../../../components/media/MediaSelector';
import { useTenantContext } from '../../../context/TenantContext';
import { useDomainQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { ShopCategorySelect } from '../../../components/admin/shop/ShopCategorySelect';

interface ProductFormData {
  title: string;
  description: string;
  content?: string;
  price: number;
  currency: string;
  stock_quantity: number;
  category_id: string;
  is_active: boolean;
  is_unique: boolean;
  dimensions?: string;
  materials: string[];
  year?: number;
  weight_kg?: number;
}

const ProductForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<{id: number, name: string}[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<{id: number, image_url: string, is_featured: boolean}[]>([]);
  const [originalExistingImages, setOriginalExistingImages] = useState<{id: number, image_url: string, is_featured: boolean}[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const { tenant } = useTenantContext();

  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain } = getFilterParams();

  // Charger les images existantes lors de l'édition
  useEffect(() => {
    if (isEditing && id) {
      const loadProductImages = async () => {
        const product = await adminGetProduct(parseInt(id), domain || '');
        if (product.images && product.images.length > 0) {
          const images = product.images.map(img => ({
            id: img.id,
            image_url: img.image_url,
            is_featured: img.is_featured
          }));
          setExistingImages(images);
          setOriginalExistingImages(images);
        }
      };
      loadProductImages();
    }
  }, [id, isEditing, domain]);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control } = useForm<ProductFormData>({
    defaultValues: {
      title: '',
      description: '',
      content: '',
      price: 0,
      currency: 'EUR',
      stock_quantity: 1,
      category_id: '',
      is_active: true,
      is_unique: false,
      materials: [],
      dimensions: '',
    },
    shouldUnregister: false
  });

  const loadCategories = async () => {
    try {
      let query = supabase.from('shop_categories').select('id, name').order('name');

      // Appliquer le filtre domaine uniquement
      if (domain) {
        query = query.eq('domain', domain);
      }

      const { data, error } = await query;
      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger les catégories.', variant: 'destructive' });
    }
  };

  useEffect(() => {
    loadCategories();
  }, [toast, domain]);

  useEffect(() => {
    if (!isEditing || !id) return;
    
    const loadProduct = async () => {
      try {
        const product = await adminGetProduct(parseInt(id), domain || '');
        console.log('Produit chargé depuis l\'API:', product);
        
        const formData: ProductFormData = {
          title: product.title,
          description: product.description || '',
          content: product.content || '',
          price: product.price || 0,
          currency: product.currency || 'EUR',
          stock_quantity: product.stock_quantity || 1,
          category_id: product.category_id?.toString() || '',
          is_active: product.status === 'available',
          is_unique: product.is_unique || false,
          dimensions: product.dimensions || '',
          materials: Array.isArray(product.materials) ? product.materials : [],
          year: product.year || undefined,
          weight_kg: product.weight_kg || undefined,
        };
        
        reset(formData);
      } catch (err) {
        console.error('Erreur lors du chargement du produit:', err);
        toast({ 
          title: 'Erreur', 
          description: 'Impossible de charger le produit.', 
          variant: 'destructive' 
        });
      }
    };
    
    loadProduct();
  }, [id, isEditing, reset, toast]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const validFiles: File[] = [];
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Fichier trop volumineux',
          description: `Le fichier ${file.name} dépasse la taille maximale de 5MB.`,
          variant: 'destructive',
        });
        return;
      }
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Format de fichier non supporté',
          description: `Le fichier ${file.name} n'est pas un format d'image valide. Utilisez JPG, PNG ou WEBP.`,
          variant: 'destructive',
        });
        return;
      }
      validFiles.push(file);
    });
    if (validFiles.length > 0) {
      setImageFiles(prev => [...prev, ...validFiles]);
    }
    // Réinitialiser l'input pour permettre la sélection des mêmes fichiers
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index: number, isExisting: boolean = false) => {
    if (isExisting) {
      setExistingImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setImageFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const setFeaturedImage = (index: number, isExisting: boolean = false) => {
    if (isExisting) {
      setExistingImages(prev => 
        prev.map((img, i) => ({
          ...img,
          is_featured: i === index
        }))
      );
    } else {
      // Si on définit une nouvelle image comme principale, on s'assure qu'aucune autre image n'est définie comme principale
      setImageFiles(prev =>
        prev.map((file, i) => {
          (file as any).is_featured = i === index;
          return file;
        })
      );
    }
  };

  const onSubmit = async (data: ProductFormData) => {
    setIsSubmitting(true);
    
    try {
      // Récupérer les paramètres de domaine
      const { domain, tenantId } = getDomainFilterParams();
      
      // 🔍 DEBUG - Vérifier le cache sessionStorage
      const cachedTenant = sessionStorage.getItem('sis_tenant_cache');
      console.log('🔍 ProductForm - Cache sessionStorage:', cachedTenant);
      console.log('🔍 ProductForm - domain:', domain);
      console.log('🔍 ProductForm - tenantId:', tenantId);
      
      // Préparer les données du produit
      const productData: Omit<ProductType, 'id' | 'created_at' | 'status' | 'slug'> & { 
        status?: 'available' | 'sold',
        slug?: string,
        domain?: string,
        tenant_id?: number
      } = {
        title: data.title,
        slug: generateSlug(data.title),
        description: data.description,
        content: data.content || null,
        price: data.price,
        currency: data.currency,
        stock_quantity: data.stock_quantity,
        category_id: parseInt(data.category_id) || null,
        status: data.is_active ? 'available' : 'sold',
        is_unique: data.is_unique,
        dimensions: data.dimensions || null,
        materials: data.materials || [],
        year: data.year || null,
        weight_kg: data.weight_kg || null,
        domain: domain || undefined,
        tenant_id: parseInt(tenantId)
      };
      
      // 🔍 DEBUG - Vérifier les données du produit
      console.log('🔍 ProductForm - Données produit:', {
        domain: productData.domain,
        tenant_id: productData.tenant_id,
        title: productData.title
      });

      if (isEditing && id) {
        // Mise à jour du produit existant
        const updateData = { ...productData };
        delete updateData.id;
        delete updateData.created_at;
        delete updateData.slug; // Ne pas mettre à jour le slug lors de l'édition
        
        // Préparer les données pour la mise à jour
        const updatePayload: any = { ...updateData };
        
        // Ajouter les nouvelles images
        if (imageFiles.length > 0) {
          updatePayload.newImages = imageFiles;
        }
        
        // Déterminer les images supprimées et les modifications
        const deletedImageIds = originalExistingImages
          .filter(original => !existingImages.some(current => current.id === original.id))
          .map(img => img.id);
        
        const updatedImageIds = existingImages
          .filter(current => {
            const original = originalExistingImages.find(o => o.id === current.id);
            return original && original.is_featured !== current.is_featured;
          })
          .map(img => img.id);
        
        if (deletedImageIds.length > 0) {
          updatePayload.deletedImageIds = deletedImageIds;
        }
        
        if (updatedImageIds.length > 0) {
          updatePayload.updatedImages = existingImages.filter(img => updatedImageIds.includes(img.id));
        }
        
        await adminUpdateProduct(parseInt(id), updatePayload);
        toast({ title: 'Succès', description: 'Produit mis à jour avec succès.' });
      } else {
        // Création d'un nouveau produit
        const createPayload: any = { ...productData };
        
        // Ajouter les images
        if (imageFiles.length > 0) {
          createPayload.images = imageFiles;
        }
        await adminCreateProduct(createPayload);
        toast({ title: 'Succès', description: 'Produit créé avec succès.' });
      }
      
      // Rediriger vers la liste des produits
      navigate('/admin/boutique');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du produit:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la sauvegarde du produit.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex items-center gap-4 mb-8">
            <Button type="button" variant="outline" size="icon" className="h-10 w-10" asChild>
              <Link to="/admin/boutique"><ArrowLeft className="h-5 w-5" /></Link>
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                {isEditing ? 'Modifier le produit' : 'Nouveau produit'}
              </h1>
              <p className="text-gray-400">Remplissez les informations ci-dessous.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-gray-200">Détails du produit</CardTitle>
                  <CardDescription className="text-gray-400">Informations de base pour identifier le produit.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title" className="text-gray-200 font-medium">Titre</Label>
                    <Input id="title" className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('title', { required: 'Le titre est requis' })} />
                    {errors.title && <p className="text-sm text-red-400 mt-1">{errors.title.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="description" className="text-gray-200 font-medium">Description</Label>
                    <Textarea id="description" className="h-32 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('description')} />
                    {errors.description && <p className="text-sm text-red-400 mt-1">{errors.description.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="content" className="text-gray-200 font-medium">Contenu détaillé</Label>
                    <Textarea id="content" className="h-48 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('content')} />
                    {errors.content && <p className="text-sm text-red-400 mt-1">{errors.content.message}</p>}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-gray-200">Tarifs et Stock</CardTitle>
                  <CardDescription className="text-gray-400">Gestion des prix et de l'inventaire.</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price" className="text-gray-200 font-medium">Prix (FCFA)</Label>
                    <Input id="price" type="number" step="0.01" className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('price', { valueAsNumber: true, required: 'Le prix est requis' })} placeholder="ex: 99.99" />
                    {errors.price && <p className="text-sm text-red-400 mt-1">{errors.price.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="stock_quantity" className="text-gray-200 font-medium">Quantité en stock</Label>
                    <Input id="stock_quantity" type="number" className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('stock_quantity', { valueAsNumber: true, min: { value: 0, message: 'La quantité doit être positive.' } })} placeholder="ex: 10" />
                    {errors.stock_quantity && <p className="text-sm text-red-400 mt-1">{errors.stock_quantity.message}</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-gray-200">Images du produit</CardTitle>
                  <CardDescription className="text-gray-400">Ajoutez plusieurs images pour ce produit. La première image sera utilisée comme image principale.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Images existantes */}
                  {existingImages.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Images existantes</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {existingImages.map((img, index) => (
                          <div key={`existing-${img.id}`} className="relative group">
                            <img
                              src={img.image_url}
                              alt={`Image ${index + 1}`}
                              className={`rounded-lg w-full h-32 object-cover ${img.is_featured ? 'ring-2 ring-blue-500' : ''}`}
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 rounded-lg transition-opacity">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFeaturedImage(index, true);
                                }}
                                className="p-1 text-white bg-blue-500 rounded-full hover:bg-blue-600"
                                title="Définir comme image principale"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z"></path>
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(index, true);
                                }}
                                className="p-1 text-white bg-red-500 rounded-full hover:bg-red-600"
                                title="Supprimer l'image"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            {img.is_featured && (
                              <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">Principale</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nouvelles images */}
                  {imageFiles.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Nouvelles images</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {imageFiles.map((file, index) => {
                          const previewUrl = (file as any).selectedUrl || URL.createObjectURL(file);
                          return (
                            <div key={`new-${index}`} className="relative group">
                              <img
                                src={previewUrl}
                                alt={`Nouvelle image ${index + 1}`}
                                className={`rounded-lg w-full h-32 object-cover ${(file as any).is_featured ? 'ring-2 ring-blue-500' : ''}`}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 rounded-lg transition-opacity">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFeaturedImage(index);
                                  }}
                                  className="p-1 text-white bg-blue-500 rounded-full hover:bg-blue-600"
                                  title="Définir comme image principale"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z"></path>
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index);
                                  }}
                                  className="p-1 text-white bg-[#D4AF37] rounded-full hover:bg-[#D4AF37]/80 transition-colors duration-200"
                                  title="Supprimer l'image"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              {(file as any).is_featured && (
                                <span className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">Principale</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Zone de drop/upload */}
                  <div className="mt-4">
                    <label
                      htmlFor="dropzone-file"
                      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 ${isUploading ? 'opacity-50' : ''}`}
                    >
                      <div className="flex flex-col items-center justify-center p-4">
                        <svg className="w-8 h-8 mb-2 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                        </svg>
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          <span className="font-medium">Ajouter des images</span><br />
                          ou glissez-déposez
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PNG, JPG, WEBP (max 5MB)</p>
                      </div>
                      <input
                        id="dropzone-file"
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileChange}
                        disabled={isUploading}
                        ref={fileInputRef}
                        multiple
                      />
                    </label>
                    
                    {/* Bouton Médiathèque */}
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowMediaSelector(true)}
                        className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
                      >
                        <ImageIcon className="mr-2 h-4 w-4" />
                        Médiathèque
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-gray-200">Caractéristiques</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="dimensions" className="text-gray-200 font-medium">Dimensions</Label>
                    <Input id="dimensions" className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('dimensions')} placeholder="ex: 50x30x20 cm" />
                    {errors.dimensions && <p className="text-sm text-red-400 mt-1">{errors.dimensions.message}</p>}
                  </div>
                  <div>
                    <Label htmlFor="materials" className="text-gray-200 font-medium">Matériaux</Label>
                    <Controller
                      name="materials"
                      control={control}
                      defaultValue={[]}
                      render={({ field: { value = [], onChange } }) => (
                        <div>
                          <div className="flex gap-2">
                            <Input 
                              id="materials" 
                              className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50 flex-1"
                              placeholder="Ajouter un matériau"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                  e.preventDefault();
                                  const newMaterials = [...(Array.isArray(value) ? value : []), e.currentTarget.value.trim()];
                                  onChange(newMaterials);
                                  e.currentTarget.value = '';
                                }
                              }}
                            />
                            <Button 
                              type="button"
                              variant="outline"
                              onClick={() => {
                                const input = document.getElementById('materials') as HTMLInputElement;
                                if (input && input.value.trim()) {
                                  const newMaterials = [...(Array.isArray(value) ? value : []), input.value.trim()];
                                  onChange(newMaterials);
                                  input.value = '';
                                }
                              }}
                            >
                              Ajouter
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(Array.isArray(value) ? value : []).map((material, index) => (
                              <div key={index} className="bg-white/10 text-white px-3 py-1 rounded-full flex items-center gap-1 border border-white/20">
                                <span className="text-sm">{material}</span>
                                <button 
                                  type="button" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const newMaterials = [...value];
                                    newMaterials.splice(index, 1);
                                    onChange(newMaterials);
                                  }}
                                  className="text-white/70 hover:text-white ml-1"
                                  aria-label="Supprimer"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="year" className="text-gray-200 font-medium">Année</Label>
                      <Input id="year" type="number" className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('year', { valueAsNumber: true })} />
                      {errors.year && <p className="text-sm text-red-400 mt-1">{errors.year.message}</p>}
                    </div>
                    <div>
                      <Label htmlFor="weight_kg" className="text-gray-200 font-medium">Poids (kg)</Label>
                      <Input id="weight_kg" type="number" step="0.01" className="bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50" {...register('weight_kg', { valueAsNumber: true })} />
                      {errors.weight_kg && <p className="text-sm text-red-400 mt-1">{errors.weight_kg.message}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-gray-200">Organisation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="category_id" className="text-gray-200 font-medium">Catégorie</Label>
                    <Controller
                      name="category_id"
                      control={control}
                      rules={{ required: 'La catégorie est requise' }}
                      render={({ field }) => (
                        <ShopCategorySelect
                          categories={categories}
                          selectedCategoryId={field.value}
                          onChange={field.onChange}
                          onCategoryCreated={(newCategory) => {
                            // Recharger les catégories après création
                            loadCategories();
                          }}
                        />
                      )}
                    />
                    {errors.category_id && <p className="text-sm text-red-400 mt-1">{errors.category_id.message}</p>}
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-white/10 p-3 shadow-sm bg-white/5">
                    <div className="space-y-0.5">
                      <Label className="text-gray-200 font-medium">Produit Actif</Label>
                      <p className="text-[0.8rem] text-gray-400">Rendre le produit visible sur la boutique.</p>
                    </div>
                    <Controller name="is_active" control={control} render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />} />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-white/10 p-3 shadow-sm bg-white/5">
                    <div className="space-y-0.5">
                      <Label className="text-gray-200 font-medium">Pièce Unique</Label>
                      <p className="text-[0.8rem] text-gray-400">Indiquer si ce produit n'est disponible qu'en un seul exemplaire.</p>
                    </div>
                    <Controller name="is_unique" control={control} render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} />} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/boutique')} disabled={isSubmitting} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-yellow-500 hover:bg-yellow-600 text-black">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Mettre à jour' : 'Créer le produit'}
            </Button>
          </div>
        </form>
        
        {/* Sélecteur de média */}
        <MediaSelector
          isOpen={showMediaSelector}
          onClose={() => setShowMediaSelector(false)}
          onSelect={(urls) => {
            // Gérer la sélection multiple
            const urlArray = Array.isArray(urls) ? urls : [urls];
            
            // Créer des objets File factices pour chaque URL sélectionnée
            const newFiles = urlArray.map(url => {
              const fileName = url.split('/').pop() || 'selected-image';
              const file = new File([], fileName, { type: 'image/jpeg' });
              (file as any).selectedUrl = url;
              return file;
            });
            
            setImageFiles(newFiles);
            setShowMediaSelector(false);
          }}
          selectedUrls={imageFiles
            .filter(file => (file as any).selectedUrl)
            .map(file => (file as any).selectedUrl)
            .concat(existingImages.map(img => img.image_url))
          }
          title="Sélectionner des images"
          allowUpload={true}
          multiSelect={true}
        />
      </div>
    </AdminLayout>
  );
};

export default ProductForm;
